import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { When } from 'react-if';
import { Form, FieldArray } from 'redux-form';
import { passengersTracker, exchangePassengersTracker } from 'metrics/user-analytics/purchase';
import { identifyUser } from 'user-analytics';
import { postNativeIdentifyUser } from 'utils/webview';
import OneSignal from 'react-onesignal';
import Loading from 'components/Loading';
import CheckoutNavigation from 'components/purchase/CheckoutNavigation';
import ModalTripInsurance from 'components/purchase/ModalContent';
import { Checkbox, Spacing, Visibility, Text, DialogMessage } from '@reservamos/elements';
import { getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input';
import { shouldRedirectToSeatSelection } from 'utils/seats';
import OverbookingMessage from 'ui/atoms/OverbookingMessage';
import WelcomeDigitalDiscountModal from 'components/search/ModalContent/WelcomeDigitalDiscountModal';
import shouldShowWelcomeDigitalDiscount from 'utils/welcomeDigitalDiscountValidation';
import { validateAndRedirectSeatSelection } from '../../../utils/seatValidation';
import PurchaserFields from '../PurchaserForm/PurchaserFields';
import DynamicPassengersForm from './DynamicPassengersForm';
import StaticPassengersForm from './StaticPassengersForm';
import PassengerPolicies from '../PassengerPolicies';
import LineSpecificTerms from '../LineSpecificTerms';
import OpenTicketMessage from '../OpenTicketMessage';
import wayIsOpenTicket, { isHybridTrip } from '../../../utils/wayIsOpenTicket';
import InsuranceDialog from '../InsuranceDialog';
import LoyaltyButton from '../../LoyaltyButton';
import PassengersStatements from './PassengersStatements';
import InsuranceAncillarySwitch from '../InsuranceAncillarySwitch';
import { autoDisableInsuranceIfIneligible } from '../../../utils/insuranceEligibility';
import { withInsuranceEligibility } from '../../../hooks/useInsuranceEligibility';
import {
  getApplicableLineTerms,
  areRequiredLineTermsAccepted,
} from '../../../hooks/purchase/useLineTerms';
import { passengerMeetsAgeRequirement } from '../../../utils/passengerUtils';
import 'styles/components/purchase/Passengers';
import OpenTicketNotice from '../OpenTicketNotice';
import ReturnCTA from '../Purchase/ReturnCTA';
import DotersBanner from '../../../ui/atoms/DotersBanner';
import MainButton from '../../../ui/atoms/MainButton';
import FlatFareSwitch from '../../Doters/FlatFare/FlatFareSwitch';
import LoyaltySelection from '../../../ui/molecules/LoyaltySelection';
import LoyaltyProgramsContext from '../../../loyalty/context/LoyaltyProgramsContext';
import userPreferences from '../../../services/userPreferences';
import { FrequentPassengersProvider } from '../../../context/FrequentPassengers';
import { getPurchaseProfileAnalyticsTraits } from '../../../utils/purchaseProfileAnalyticsTraits';

const fragment = PropTypes.shape({
  id: PropTypes.string.isRequired,
  transportType: PropTypes.string.isRequired,
});

const location = PropTypes.shape({
  cityName: PropTypes.string,
});

const way = PropTypes.shape({
  fragments: PropTypes.arrayOf(fragment),
  selectedSeats: PropTypes.any,
  origin: location,
  destination: location,
});

const propTypes = {
  purchase: PropTypes.shape({
    isExchange: PropTypes.bool.isRequired,
    allowsSeatSelection: PropTypes.bool.isRequired,
    departs: way.isRequired,
    hasSelectedSeats: PropTypes.bool.isRequired,
    isFetchingDetails: PropTypes.bool.isRequired,
    isLockingTickets: PropTypes.bool.isRequired,
    roundTrip: PropTypes.bool.isRequired,
    hasDetails: PropTypes.bool.isRequired,
    hasTickets: PropTypes.bool.isRequired,
    token: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    passengerSelection: PropTypes.object,
    passengers: PropTypes.array,
    returns: way,
    savingPassengers: PropTypes.bool.isRequired,
    qualifiesForInsurance: PropTypes.bool.isRequired,
    updatingInsurance: PropTypes.bool.isRequired,
    openTicket: PropTypes.bool.isRequired,
    insuranceDurationDays: PropTypes.number,
    insuranceOutgoingUnitAmount: PropTypes.number,
    insuranceIncomingUnitAmount: PropTypes.number,
    insurancesChecked: PropTypes.bool,
    ticketsErrorType: PropTypes.string,
    wantsInsurance: PropTypes.bool.isRequired,
    isUpdatingFlatFare: PropTypes.bool.isRequired,
    walletType: PropTypes.string,
    isUpdatingWalletType: PropTypes.bool,
    isUpdating: PropTypes.bool,
    preSelectedSeats: PropTypes.shape({
      departure: PropTypes.array,
      return: PropTypes.array,
    }),
  }),
  passengersForm: PropTypes.object.isRequired,
  frequentPassengers: PropTypes.array.isRequired,
  purchaser: PropTypes.object.isRequired,
  busCategories: PropTypes.array.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  savePassengers: PropTypes.func.isRequired,
  autofillFirstPassenger: PropTypes.func.isRequired,
  autofillInsurances: PropTypes.func.isRequired,
  setInsurancesChecked: PropTypes.func.isRequired,
  transitionTo: PropTypes.func.isRequired,
  returnTo: PropTypes.func.isRequired,
  clearTicketsErrors: PropTypes.func.isRequired,
  deletePassenger: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  insuranceMessage: PropTypes.string,
  insuranceAmount: PropTypes.string,
  insuranceAmountIsUnitPrice: PropTypes.bool,
  insuranceAmountWithTaxes: PropTypes.string,
  onToggleWantsInsurance: PropTypes.func.isRequired,
  isExchange: PropTypes.bool,
  features: PropTypes.object.isRequired,
  env: PropTypes.object.isRequired,
  initialValues: PropTypes.object,
  user: PropTypes.object,
  datesOfBirthAreValid: PropTypes.func,
  showDateOfBirthWarning: PropTypes.func,
  showIntermunicipalDateOfBirthWarning: PropTypes.func,
  showPetFriendlySeatsWarn: PropTypes.func,
  showAcceptTermsWarning: PropTypes.func,
  showLineTermsWarning: PropTypes.func,
  petFriendlyQuantityIsValid: PropTypes.func,
  isLogged: PropTypes.bool,
  isAccountThePassenger: PropTypes.bool,
  needsLoyaltySelector: PropTypes.bool,
  isUpdatingFlatFare: PropTypes.bool,
  clearError: PropTypes.func,
  isUserAuthenticated: PropTypes.bool,
  errors: PropTypes.errors,
  overbooking: PropTypes.bool,
  overbookingTripType: PropTypes.string,
  copies: PropTypes.object,
  insuranceEligibility: PropTypes.shape({
    hasIneligibleDoc: PropTypes.bool.isRequired,
    passengerDocs: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  fetchProductCatalog: PropTypes.func.isRequired,
};

/**
 * Passengers component.
 * Render passengers form step, insurance prompts, and related controls.
 * @param {object} props - purchase, passengersForm, purchaser, features, env, etc.
 */
class Passengers extends Component {
  constructor(props) {
    super(props);

    this.state = {
      autofillEnabled: false,
      insuranceDialogVisible: false,
      acceptedTerms: true,
      acceptedLineTerms: {},
      lineTermsError: false,
      showTicketReviewModal: false,
      lockingTicketsFinished: false,
      waitingForUpdate: false,
    };

    this.pendingFields = null;
    this.modalConfirmed = false;

    this.onSubmitPassengers = this.onSubmitPassengers.bind(this);
    this.togglePassengerAutofill = this.togglePassengerAutofill.bind(this);
    this.passengersInsurancesMissing = this.passengersInsurancesMissing.bind(this);
    this.selectAllInsurances = this.selectAllInsurances.bind(this);
    this.needsInsuranceReminder = this.needsInsuranceReminder.bind(this);
    this.toggleWantsInsurance = this.toggleWantsInsurance.bind(this);
    this.insuranceReminderGFA = this.insuranceReminderGFA.bind(this);
    this.insuranceReminderBrasilia = this.insuranceReminderBrasilia.bind(this);
    this.insuranceReminderCommon = this.insuranceReminderCommon.bind(this);
    this.insuranceReminderDefault = this.insuranceReminderDefault.bind(this);
    this.activateWantsInsurance = this.activateWantsInsurance.bind(this);
    this.toggleAcceptTerms = this.toggleAcceptTerms.bind(this);
    this.toggleLineTerms = this.toggleLineTerms.bind(this);
    this.handleTicketReviewModalConfirm = this.handleTicketReviewModalConfirm.bind(this);
    this.handleTicketReviewModalQuit = this.handleTicketReviewModalQuit.bind(this);
    this.applyAllInsurances = this.applyAllInsurances.bind(this);
  }

  componentDidMount() {
    const { clearError, errors, purchase, features, transitionTo, fetchProductCatalog } =
      this.props;
    const { USE_SEAT_BASED_PRICING, PASSENGER_INSURANCE_BY_DEFAULT } = features;
    /**
     * Clean any previous fixed errors generated by other purchase steps.
     * We only clean fixed errors, so other type of errors can still be shown correctly.
     * Errors with a type different to fixed are rendered (and cleaned) by the Notification component.
     */
    if (errors.code === 0 && errors.alertType === 'fixed') {
      clearError();
    }

    if (
      shouldRedirectToSeatSelection({
        useSeatsBasedPricing: USE_SEAT_BASED_PRICING,
        purchase,
      })
    ) {
      transitionTo(`/purchase/${purchase.token}/seats/departure`);
      return;
    }

    // Redirect to previous step if tickets are not set
    // setTimeout to queue as last, so is executed after loyalty (prev siempre-plus) user is set
    setTimeout(() => {
      this.redirectIfAppropriate();

      // If feature flag for default is on
      if (PASSENGER_INSURANCE_BY_DEFAULT) this.applyAllInsurances();
    }, 1);

    // The fetchProductCatalog thunk gates itself on the products cart flag.
    fetchProductCatalog();
  }

  componentWillReceiveProps(nextProps) {
    const { purchase, transitionTo, frequentPassengers } = this.props;
    const { features } = nextProps;
    const { token, isLockingTickets, isUpdating } = purchase;
    const { lockingTicketsFinished } = this.state;
    const { SHOW_FREQUENT_PASSENGERS } = features;

    // Check if locking tickets just finished or updating just finished
    const lockingTicketsJustFinished = isLockingTickets && !nextProps.purchase.isLockingTickets;
    const updatingJustFinished = isUpdating && !nextProps.purchase.isUpdating;

    // Update state to keep track of transitions
    if (lockingTicketsJustFinished) {
      this.setState({ lockingTicketsFinished: true, waitingForUpdate: true });
    }

    if (updatingJustFinished) {
      this.setState({ waitingForUpdate: false }); // reset waiting for update
      if (lockingTicketsFinished && nextProps.purchase.hasTickets) {
        // user-analytics "Passengers Created" (and exchange equivalent)
        const isExchangePurchase = nextProps.purchase.isExchange;

        if (isExchangePurchase) {
          exchangePassengersTracker(nextProps.purchase);
        } else {
          const selectedFrequentPassengers = frequentPassengers.filter(
            (passenger) => passenger.isSelected,
          );
          passengersTracker({ ...nextProps.purchase, selectedFrequentPassengers });
        }

        const hasFrequentPassengers =
          SHOW_FREQUENT_PASSENGERS && Boolean(frequentPassengers.length);
        transitionTo(`/purchase/${token}/checkout?frequentPassengers=${hasFrequentPassengers}`);
      }
    }
  }

  shouldComponentUpdate(nextProps) {
    // Don't update if purchase is not in attempt state since it
    // will get redirected to the appropriate route
    return nextProps.purchase.state === 'attempt';
  }

  componentDidUpdate(prevProps) {
    const { autofillFirstPassenger, clearTicketsErrors, returnTo, purchaser, submit, purchase } =
      this.props;
    const { insurancesChecked, ticketsErrorType, token } = purchase;
    const {
      purchaserFirstName,
      purchaserLastName,
      email,
      purchaserSecondLastName,
      documentType,
      documentId,
    } = purchaser;
    const { autofillEnabled } = this.state;
    const purchaserChanged =
      prevProps.purchaser.purchaserFirstName !== purchaserFirstName ||
      prevProps.purchaser.purchaserLastName !== purchaserLastName ||
      prevProps.purchaser.purchaserSecondLastName !== purchaserSecondLastName ||
      prevProps.purchaser.email !== email;

    if (autofillEnabled && purchaserChanged) {
      autofillFirstPassenger(
        purchaserFirstName,
        purchaserLastName,
        purchaserSecondLastName,
        documentType,
        documentId,
        email,
      );
    }

    if (ticketsErrorType) {
      if (ticketsErrorType === 'occupied') returnTo(`/purchase/${token}/seats/departure`);
      clearTicketsErrors();
    }

    if (!prevProps.purchase.insurancesChecked && insurancesChecked) {
      submit();
    }

    // Auto-disable insurance if any passenger has an ineligible document type
    const { features, onToggleWantsInsurance, insuranceEligibility } = this.props;
    const { insuranceDialogVisible } = this.state;
    autoDisableInsuranceIfIneligible({
      purchase,
      features,
      onToggleWantsInsurance,
      getInsuranceEligibility: () => insuranceEligibility,
      additionalConditions: {
        INSURANCE_ON_CHECKOUT_ONLY: features.INSURANCE_ON_CHECKOUT_ONLY,
      },
      onCloseInsuranceModal: insuranceDialogVisible
        ? () => this.setState({ insuranceDialogVisible: false })
        : undefined,
    });
  }

  componentWillUnmount() {
    this.setState({
      insuranceDialogVisible: false,
    });
  }

  handleTicketReviewModalConfirm() {
    this.setState({ showTicketReviewModal: false });
    if (this.pendingFields) {
      this.modalConfirmed = true;
      this.onSubmitPassengers(this.pendingFields);
      this.pendingFields = null;
    }
  }

  handleTicketReviewModalQuit() {
    this.setState({ showTicketReviewModal: false });
    this.pendingFields = null;
  }

  onSubmitPassengers(fields) {
    const {
      purchase,
      savePassengers,
      user,
      isExchange,
      features,
      env,
      datesOfBirthAreValid,
      showDateOfBirthWarning,
      showIntermunicipalDateOfBirthWarning,
      petFriendlyQuantityIsValid,
      showPetFriendlySeatsWarn,
      showAcceptTermsWarning,
      showLineTermsWarning,
      isUserAuthenticated,
      copies,
    } = this.props;
    const {
      departs,
      returns,
      roundTrip,
      token,
      insuranceIncomingUnitAmount,
      insuranceOutgoingUnitAmount,
    } = purchase;
    const { acceptedTerms, acceptedLineTerms } = this.state;
    const trips = {};

    if (features.SHOW_ACCEPT_POLICIES_CHECKBOX && !acceptedTerms) {
      showAcceptTermsWarning();
      return;
    }

    const applicableLineTerms = getApplicableLineTerms({ purchase, copies, features });
    if (
      !areRequiredLineTermsAccepted({ applicableTerms: applicableLineTerms, acceptedLineTerms })
    ) {
      this.setState({ lineTermsError: true });
      showLineTermsWarning();
      return;
    }

    // Check if there are intermunicipal_free_pass passengers aged 65+ and show modal
    if (features.SHOW_TICKET_REVIEW_MODAL && !this.modalConfirmed) {
      const { passengersForm } = this.props;
      const passengersArr = passengersForm?.passengers?.values?.passengers || [];
      /* eslint-disable function-paren-newline */
      const intermunicipalPassengers = passengersArr.filter((passenger) =>
        passenger?.busCategory?.includes('intermunicipal_free_pass'),
      );
      /* eslint-enable function-paren-newline */

      // Only show modal if there are intermunicipal passengers who meet age requirement (65+)
      const hasValidIntermunicipal = intermunicipalPassengers.some(passengerMeetsAgeRequirement);

      if (hasValidIntermunicipal) {
        this.setState({ showTicketReviewModal: true });
        // Store fields for later submission
        this.pendingFields = fields;
        return;
      }
    }

    // prettier-ignore
    if (features.SHOW_BIRTH_DATE_WARNING && !datesOfBirthAreValid()) {
      const { passengersForm } = this.props;
      const passengersArr = passengersForm?.passengers?.values?.passengers || [];
      const hasIntermunicipal = passengersArr.some((p) =>
        p?.busCategory?.includes('intermunicipal_free_pass'));
      if (hasIntermunicipal && typeof showIntermunicipalDateOfBirthWarning === 'function') {
        showIntermunicipalDateOfBirthWarning();
      } else {
        showDateOfBirthWarning();
      }
      return;
    }

    if (!petFriendlyQuantityIsValid()) {
      showPetFriendlySeatsWarn();
      return;
    }

    if (
      ((isExchange && features.CAN_ADD_EXCHANGE_INSURANCE) || !isExchange) &&
      this.needsInsuranceReminder() &&
      (insuranceIncomingUnitAmount > 0 || insuranceOutgoingUnitAmount > 0)
    ) {
      return;
    }

    trips.departs = departs.fragments.map(({ id: fragmentId, transportType }) => ({
      id: fragmentId,
      type: transportType,
      selectedSeats: departs.selectedSeats,
    }));

    if (roundTrip) {
      trips.returns = returns.fragments.map(
        ({ id: returnFragmentId, transportType: returnTransportType }) => ({
          id: returnFragmentId,
          type: returnTransportType,
          selectedSeats: returns.selectedSeats,
        }),
      );
    }

    if (features.PURCHASER_FORM_ON === 'PASSENGERS') {
      const hasOneSignalEnabled = env.oneSignal.enabled;
      const userEmail = fields.email || (fields.passengers[0] ? fields.passengers[0].email : '');
      const phoneCode = getCountryCallingCode(fields.phoneCountry);
      const phoneNumber = parsePhoneNumber(`+${phoneCode}${fields.phone}`);

      if (hasOneSignalEnabled) {
        OneSignal.setSMSNumber(`${phoneNumber.number}`);
        OneSignal.setExternalUserId(phoneNumber.nationalNumber);
        OneSignal.setEmail(userEmail);
        OneSignal.sendTags({ purchase_token: token });
      }
      identifyUser(phoneNumber.nationalNumber, {
        '$first_name': fields.purchaserFirstName,
        '$last_name': fields.purchaserLastName,
        '$name': `${fields.purchaserFirstName} ${fields.purchaserLastName}`,
        '$email': userEmail,
        '$phone': phoneNumber.nationalNumber,
        'User Type': isUserAuthenticated ? 'Registered' : 'Anonymous',
        '$onesignal_user_id': phoneNumber.nationalNumber,
        ...(user ? { $membership_id: user.spCard || user.dotersId } : {}),
        ...getPurchaseProfileAnalyticsTraits(env.brand, {
          passenger: fields.passengers[0],
          purchaser: fields,
        }),
      });
      postNativeIdentifyUser({
        phone: phoneNumber.nationalNumber,
        email: userEmail,
        firstName: fields.purchaserFirstName,
        lastName: fields.purchaserLastName,
      });
    }
    userPreferences
      .createAnonymousProfile({
        email: fields.passengers[0].email,
        phone: fields.passengers[0].phone,
        ...getPurchaseProfileAnalyticsTraits(env.brand, {
          passenger: fields.passengers[0],
          purchaser: fields,
        }),
      })
      .catch((error) => {
        console.error(error);
      });

    window.scrollTo(0, 0);
    this.setState({ insuranceDialogVisible: false });

    savePassengers(fields, trips);
  }

  getLoyaltyHolderName() {
    const { purchase, user } = this.props;
    const { walletType } = purchase;
    const types = {
      siempreplus: 'siempreplus',
      doters: 'doters',
      costapass: 'costapass',
      travelpass: 'travelpass',
    };

    // When purchase.walletType is null (e.g. after saving a Doters flexible guest
    // passenger), fall back to the active session's loyaltyName from siemprePlus.
    // getUserByWalletType always returns the siemprePlus user via its 'default' key,
    // so user?.loyaltyName is available even when walletType is null.
    return types[walletType] || user?.loyaltyName?.toLowerCase() || null;
  }

  togglePassengerAutofill() {
    const { autofillEnabled: oldAutofillEnabled } = this.state;
    const { autofillFirstPassenger, purchaser } = this.props;
    const {
      purchaserFirstName,
      purchaserLastName,
      purchaserSecondLastName,
      documentType,
      documentId,
      email,
    } = purchaser;
    const autofillEnabled = !oldAutofillEnabled;

    if (autofillEnabled) {
      autofillFirstPassenger(
        purchaserFirstName,
        purchaserLastName,
        purchaserSecondLastName,
        documentType,
        documentId,
        email,
      );
    } else {
      autofillFirstPassenger('', '', '', '', '');
    }

    this.setState({ autofillEnabled });
  }

  redirectIfAppropriate() {
    const { purchase, returnTo } = this.props;

    validateAndRedirectSeatSelection(purchase, {
      redirectFn: returnTo,
      type: 'passengers',
    });
  }

  insuranceReminderCommon() {
    const { insuranceDialogVisible } = this.state;

    if (this.hasInsurancesToAdd() && !insuranceDialogVisible) {
      this.setState({ insuranceDialogVisible: true });
      return true;
    }
    return false;
  }

  insuranceReminderGFA() {
    return this.insuranceReminderCommon();
  }

  insuranceReminderBrasilia() {
    return this.insuranceReminderCommon();
  }

  insuranceReminderDefault() {
    const { insuranceDialogVisible } = this.state;
    const { features } = this.props;

    if (
      features.PASSENGERS_INSURANCE_ENABLED &&
      !insuranceDialogVisible &&
      this.passengersInsurancesMissing()
    ) {
      this.setState({ insuranceDialogVisible: true });
      return true;
    }
    return false;
  }

  needsInsuranceReminder() {
    const { env } = this.props;
    const brandReminder = {
      'gfa': this.insuranceReminderGFA,
      'roll-bits': this.insuranceReminderGFA,
      'default': this.insuranceReminderDefault,
      'brasilia': this.insuranceReminderBrasilia,
    };

    const currentReminder = brandReminder[env.brand] ?? brandReminder.default;

    return currentReminder();
  }

  activateWantsInsurance() {
    const {
      purchase: { token },
      onToggleWantsInsurance,
    } = this.props;

    onToggleWantsInsurance(token, true);
  }

  toggleWantsInsurance(event) {
    const {
      purchase: { token },
      onToggleWantsInsurance,
    } = this.props;

    onToggleWantsInsurance(token, event.target.checked);
  }

  hasInsurancesToAdd() {
    const { purchase, features, insuranceEligibility } = this.props;
    const { qualifiesForInsurance, wantsInsurance, updatingInsurance } = purchase;

    if (updatingInsurance) return false;
    if (!qualifiesForInsurance) return false;

    const { hasIneligibleDoc } = insuranceEligibility;
    if (hasIneligibleDoc) return false;

    if (features.PASSENGERS_INSURANCE_ENABLED) {
      return this.passengersInsurancesMissing();
    }
    return !wantsInsurance;
  }

  passengersInsurancesMissing() {
    const {
      passengersForm: { passengers },
      purchase: { departs, returns, roundTrip },
    } = this.props;

    const departureIsOpen = wayIsOpenTicket(departs) === true;
    if (!departureIsOpen && passengers.values.passengers.some((p) => !p.wantsOutgoingInsurance)) {
      return true;
    }

    const returnsIsOpen = wayIsOpenTicket(returns) === true;
    if (
      roundTrip &&
      !returnsIsOpen &&
      passengers.values.passengers.some((p) => !p.wantsIncomingInsurance)
    ) {
      return true;
    }

    return false;
  }

  selectAllInsurances() {
    const { setInsurancesChecked } = this.props;

    this.applyAllInsurances();

    setInsurancesChecked();

    this.setState({ insuranceDialogVisible: false });
  }

  applyAllInsurances() {
    const {
      autofillInsurances,
      passengersForm: { passengers },
      purchase: {
        departs,
        returns,
        roundTrip,
        qualifiesForInsurance,
        insuranceIncomingUnitAmount,
        insuranceOutgoingUnitAmount,
      },
      env,
    } = this.props;

    if (
      !qualifiesForInsurance ||
      (insuranceIncomingUnitAmount <= 0 && insuranceOutgoingUnitAmount <= 0)
    )
      return false;

    if (env.brand === 'gfa' || env.brand === 'roll-bits' || env.brand === 'brasilia') {
      this.activateWantsInsurance();
    } else {
      const selectDepartureInsurances = !wayIsOpenTicket(departs);
      const selectReturnsInsurances = roundTrip && !wayIsOpenTicket(returns);
      passengers.values.passengers.forEach((_, passengerIndex) => {
        autofillInsurances(passengerIndex, selectDepartureInsurances, selectReturnsInsurances);
      });
    }
  }

  toggleAcceptTerms() {
    this.setState((prevState) => ({ acceptedTerms: !prevState.acceptedTerms }));
  }

  toggleLineTerms(lineId) {
    this.setState((prevState) => ({
      acceptedLineTerms: {
        ...(prevState.acceptedLineTerms || {}),
        [lineId]: !prevState.acceptedLineTerms?.[lineId],
      },
      lineTermsError: false,
    }));
  }

  render() {
    const {
      autofillEnabled,
      insuranceDialogVisible,
      acceptedTerms,
      acceptedLineTerms,
      lineTermsError,
      showTicketReviewModal,
      waitingForUpdate,
    } = this.state;
    const {
      busCategories,
      handleSubmit,
      submit,
      deletePassenger,
      purchase,
      purchase: { wantsInsurance, qualifiesForInsurance, updatingInsurance },
      insuranceMessage,
      insuranceAmount,
      insuranceAmountIsUnitPrice,
      insuranceAmountWithTaxes,
      t,
      isExchange,
      purchaser,
      initialValues,
      features,
      user,
      showPetFriendlySeatsWarn,
      isLogged,
      isAccountThePassenger,
      needsLoyaltySelector,
      autofillFirstPassenger,
      isUpdatingFlatFare,
      overbooking,
      insuranceEligibility,
      overbookingTripType,
    } = this.props;

    const showPurchaserForm = features.PURCHASER_FORM_ON === 'PASSENGERS';

    const {
      isFetchingDetails,
      savingPassengers,
      isLockingTickets,
      allowsSeatSelection,
      departs,
      returns,
      insuranceDurationDays,
      insuranceOutgoingUnitAmount,
      insuranceIncomingUnitAmount,
      roundTrip,
      isUpdatingWalletType,
      isUpdating,
    } = purchase;

    /**
     * Loading screen with the context of saving passenger will be shown when:
     * - Saving passengers, Locking tickets: after user clicks on next button.
     * - Waiting for update: when tickets are locked and waiting for purchase to be updated.
     */
    if (savingPassengers || isLockingTickets || waitingForUpdate) {
      return <Loading loadingText={t('passengers', { context: 'saving' })} />;
    }

    const isDepartureOpen = wayIsOpenTicket(departs);
    const isReturnOpen = wayIsOpenTicket(returns);
    const hasOpenTicket = isDepartureOpen || isReturnOpen;

    const exchangeInsuranceValidation =
      !isExchange || (isExchange && features.CAN_ADD_EXCHANGE_INSURANCE);

    const SHOW_PURCHASER_FORM = features.PURCHASER_FORM_ON === 'PASSENGERS';

    // Checking if it is a hybrid trip to show the not insurance message
    const isHybrid = isHybridTrip(departs, returns);

    const showMessageNotInsuranceOpenTicket = isHybrid && !features.OPENT_TICKET_INSURANCE_ENABLED;
    const showMessageJustAdult = isHybrid && features.JUST_ADULT_OPEN_TICKET;
    const isLoadingNextButton =
      isUpdatingWalletType || updatingInsurance || isUpdating || isUpdatingFlatFare;

    const shouldCalculateInsuranceEligibility =
      exchangeInsuranceValidation &&
      qualifiesForInsurance &&
      !features.PASSENGERS_INSURANCE_ENABLED &&
      !features.INSURANCE_ON_CHECKOUT_ONLY;
    const { hasIneligibleDoc } = shouldCalculateInsuranceEligibility ? insuranceEligibility : {};
    const disableInsuranceSwitch = hasIneligibleDoc;

    const showWelcomeDigitalDiscountBanner = shouldShowWelcomeDigitalDiscount({
      originCityName: departs.origin?.cityName,
      destinationCityName: departs.destination?.cityName,
    });

    const {
      costaPass: { config: costaPassConfig, userIsLoggedIn: userIsLoggedInWithCostapass } = {},
    } = this.context;
    const mobileLoginSection = (
      <Visibility type="showForMobileOnly">
        <Spacing vertical>
          {features.DOTERS_ENABLED && (
            <div className="login-container">
              <DotersBanner show={Boolean(!isLogged)} />
            </div>
          )}
          {features.COSTAPASS_ENABLED && !userIsLoggedInWithCostapass && (
            <LoyaltyButton
              id="siempre-plus-widget-passengers-button-costapass"
              insideIcon
              extraConfig={costaPassConfig}
            />
          )}
        </Spacing>
      </Visibility>
    );

    return (
      <div className="passengers-container">
        <When condition={needsLoyaltySelector}>
          <LoyaltySelection />
        </When>
        <When condition={!needsLoyaltySelector}>
          <Spacing size="XXL" vertical>
            {overbooking && <OverbookingMessage tripType={overbookingTripType} />}
            {hasOpenTicket && !isHybrid && <OpenTicketMessage isLoggedIn={Boolean(user)} />}
            <ReturnCTA type="card" />

            {showWelcomeDigitalDiscountBanner && (
              <Visibility type="showForMobileOnly">
                <WelcomeDigitalDiscountModal screen="passengers" />
              </Visibility>
            )}

            <Form onSubmit={handleSubmit(this.onSubmitPassengers)}>
              {SHOW_PURCHASER_FORM && (
                <>
                  <div className="passenger-detail-title">
                    <h2>{t('seats:purchaser_info')}</h2>
                  </div>

                  <PurchaserFields
                    initialValues={initialValues}
                    purchaser={purchaser}
                    isExchange={isExchange}
                  />
                </>
              )}

              {insuranceDialogVisible &&
                !insuranceEligibility.hasIneligibleDoc &&
                (insuranceIncomingUnitAmount > 0 || insuranceOutgoingUnitAmount > 0) && (
                  <>
                    {features.PASSENGERS_INSURANCE_ENABLED ? (
                      <InsuranceDialog
                        onAccept={this.selectAllInsurances}
                        onReject={submit}
                        insuranceDuration={insuranceDurationDays}
                        insuranceOutgoingAmount={insuranceOutgoingUnitAmount}
                        insuranceIncomingAmount={insuranceIncomingUnitAmount}
                      />
                    ) : (
                      <ModalTripInsurance
                        onAccept={this.selectAllInsurances}
                        onReject={submit}
                        message={insuranceMessage}
                      />
                    )}
                  </>
                )}

              <Spacing vertical size="XL">
                <FrequentPassengersProvider>
                  <FieldArray
                    name="passengers"
                    component={!allowsSeatSelection ? DynamicPassengersForm : StaticPassengersForm}
                    busCategories={busCategories}
                    updatingCategories={isFetchingDetails}
                    autofillEnabled={autofillEnabled}
                    toggleAutofill={this.togglePassengerAutofill}
                    deletePassenger={deletePassenger}
                    isOpenTicket={isDepartureOpen && (!roundTrip || isReturnOpen)}
                    insuranceOutgoingUnitAmount={insuranceOutgoingUnitAmount}
                    insuranceIncomingUnitAmount={insuranceIncomingUnitAmount}
                    isExchange={isExchange}
                    isRoundTrip={roundTrip}
                    user={user}
                    showPetFriendlySeatsWarn={showPetFriendlySeatsWarn}
                    loyaltyHolderName={this.getLoyaltyHolderName()}
                    isLogged={isLogged}
                    initialValues={initialValues.passengers}
                    dynamicInitialValues={initialValues.passengers}
                    isAccountThePassenger={isAccountThePassenger}
                    autofillFirstPassenger={autofillFirstPassenger}
                    overbooking={overbooking}
                  />
                </FrequentPassengersProvider>
              </Spacing>
            </Form>

            {showMessageNotInsuranceOpenTicket && (
              <OpenTicketNotice
                t={t}
                isReturn={isDepartureOpen}
                messageJustAdult={showMessageJustAdult}
                messageNotInsurance={showMessageNotInsuranceOpenTicket}
              />
            )}

            {shouldCalculateInsuranceEligibility && (
              <>
                <InsuranceAncillarySwitch
                  id="insurance"
                  checked={wantsInsurance}
                  isLoading={updatingInsurance}
                  onChange={this.toggleWantsInsurance}
                  message={insuranceMessage}
                  price={insuranceAmount}
                  singlePrice={insuranceAmountIsUnitPrice && !showMessageNotInsuranceOpenTicket}
                  plusTaxes
                  fullPrice={insuranceAmountWithTaxes}
                  disabled={disableInsuranceSwitch}
                />
                {disableInsuranceSwitch && (
                  <Spacing vertical size="XS">
                    <Text size="S" color="error">
                      {t('insurance:not_available_for_selected_document', {
                        defaultValue:
                          'El seguro no está disponible para el/los documentos seleccionados.',
                      })}
                    </Text>
                  </Spacing>
                )}
              </>
            )}

            <PassengersStatements />

            {!showPurchaserForm && mobileLoginSection}

            <Spacing vertical size="XS">
              {features.POLICIES_DISCLAIMER_LOCATION === 'PASSENGERS' &&
                (features.SHOW_ACCEPT_POLICIES_CHECKBOX ? (
                  <Checkbox
                    id="passenger-policies-checkbox"
                    onChange={this.toggleAcceptTerms}
                    checked={acceptedTerms}
                    label={<PassengerPolicies />}
                  />
                ) : (
                  <PassengerPolicies />
                ))}

              <LineSpecificTerms
                acceptedTerms={acceptedLineTerms}
                onToggle={this.toggleLineTerms}
                hasError={lineTermsError}
              />
            </Spacing>

            <FlatFareSwitch />

            <CheckoutNavigation>
              <MainButton
                label={t('general:next')}
                isDisabled={isLoadingNextButton}
                isLoading={isLoadingNextButton}
                onClick={submit}
              />
            </CheckoutNavigation>
          </Spacing>
        </When>
        {showTicketReviewModal && (
          <DialogMessage
            onClickPrimary={this.handleTicketReviewModalConfirm}
            onClickSecondary={this.handleTicketReviewModalQuit}
            primaryButton={t('passengers:ticket_review.primary_button')}
            secondaryButton={t('passengers:ticket_review.secondary_button')}
            title={t('passengers:ticket_review.title')}
          >
            <Text>{t('passengers:ticket_review.description')}</Text>
            <Text weight="bold">{t('passengers:ticket_review.confirmation')}</Text>
          </DialogMessage>
        )}
      </div>
    );
  }
}

Passengers.contextType = LoyaltyProgramsContext;
Passengers.propTypes = propTypes;

export default withInsuranceEligibility(Passengers);
